import React, { useState, useEffect } from 'react';
import './Admins.css';
import { toast } from 'react-toastify';
import { authAPI } from '../../config/auth-api';
import { getApiOrigin } from '../../config/api';
import { Pencil, Trash2 } from 'lucide-react';
import DeleteConfirmationModal from '../Common/DeleteConfirmationModal/DeleteConfirmationModal';
import { helpers } from '../../utils/helpers';
import { ADMIN_PERMISSIONS, PERMISSION_LABELS } from '../../constants/permissions';
import { isIndiaClient } from '../../utils/client';

const cleanQatarPhone = (phone) => {
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('974')) {
    cleaned = cleaned.slice(3);
  } else if (cleaned.startsWith('00974')) {
    cleaned = cleaned.slice(5);
  }
  return cleaned.slice(0, 8);
};

const cleanIndiaPhone = (phone) => {
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('91')) {
    cleaned = cleaned.slice(2);
  } else if (cleaned.startsWith('0091')) {
    cleaned = cleaned.slice(4);
  } else if (cleaned.startsWith('0') && cleaned.length === 11) {
    cleaned = cleaned.slice(1);
  }
  return cleaned.slice(0, 10);
};

const ALL_PERMISSION_VALUES = Object.values(ADMIN_PERMISSIONS);

const formatPermissionLabel = (perm) =>
  PERMISSION_LABELS[perm]
  || perm.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const regionsFromAdmin = (admin) => {
  const raw = [
    ...(Array.isArray(admin?.regions) ? admin.regions : []),
    ...(Array.isArray(admin?.storeIds) ? admin.storeIds : []),
  ];
  const selected = [];
  raw.forEach((value) => {
    const key = String(value || '').toLowerCase();
    if ((key === 'qatar' || key === 'ecatcho') && !selected.includes('qatar')) selected.push('qatar');
    if ((key === 'india' || key === 'clickncart') && !selected.includes('india')) selected.push('india');
  });
  return selected.length ? selected : ['qatar'];
};

const Admins = ({ adminData, setAdminData }) => {
  const isIndia = isIndiaClient(adminData);
  const countryCode = isIndia ? '+91' : '+974';
  const phoneDigits = isIndia ? 10 : 8;
  const cleaner = isIndia ? cleanIndiaPhone : cleanQatarPhone;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    profilePicture: '',
    profilePictureFile: null,
  });
  const [regions, setRegions] = useState(['qatar']);

  // Permissions
  const [permissionAccessType, setPermissionAccessType] = useState('all');
  const [selectedPermissions, setSelectedPermissions] = useState([...ALL_PERMISSION_VALUES]);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [tempSelectedPermissions, setTempSelectedPermissions] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState('');

  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_ADMINS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_ADMINS);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_ADMINS);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, adminId: null, adminName: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAdmins = async (page = 1) => {
    try {
      setAdminLoading(true);
      setAdminError(null);

      const response = await authAPI.getAdminsList(page, 20);

      if (response.success) {
        setAdmins(response.data || []);
        setPagination(response.pagination);
      } else {
        setAdminError(response.error || 'Failed to fetch admins');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setAdminError('Error fetching admins: ' + error.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const revokePreviewUrl = (url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => {
      revokePreviewUrl(prev.profilePicture);
      return {
        ...prev,
        profilePicture: previewUrl,
        profilePictureFile: file,
      };
    });
  };

  useEffect(() => {
    fetchAdmins(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const cleaner = isIndia ? cleanIndiaPhone : cleanQatarPhone;
      setFormData((prev) => ({
        ...prev,
        phoneNumber: cleaner(value),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Phone is optional
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length === phoneDigits;
  };

  const openPermissionsModal = () => {
    setTempSelectedPermissions([...selectedPermissions]);
    setPermissionSearch('');
    setIsPermissionsModalOpen(true);
  };

  const savePermissionsSelection = () => {
    setSelectedPermissions([...tempSelectedPermissions]);
    setIsPermissionsModalOpen(false);
  };

  const toggleTempPermission = (p) => {
    setTempSelectedPermissions((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const filteredPermissions = [...ALL_PERMISSION_VALUES].filter((p) => {
    const formatted = formatPermissionLabel(p).toLowerCase();
    const raw = p.toLowerCase();
    const query = permissionSearch.toLowerCase();
    return formatted.includes(query) || raw.includes(query);
  });

  const allTempSelected = filteredPermissions.length > 0 && filteredPermissions.every((p) => tempSelectedPermissions.includes(p));

  const toggleAllPermissions = () => {
    if (allTempSelected) {
      setTempSelectedPermissions((prev) => prev.filter((p) => !filteredPermissions.includes(p)));
    } else {
      setTempSelectedPermissions((prev) => [...new Set([...prev, ...filteredPermissions])]);
    }
  };

  const handleEdit = (admin) => {
    setEditMode(true);
    setSelectedAdminId(admin._id);
    const cleaner = isIndia ? cleanIndiaPhone : cleanQatarPhone;
    setFormData({
      name: admin.name || '',
      email: admin.email || '',
      phoneNumber: cleaner(admin.phoneNumber || ''),
      profilePicture: admin.profilePicture || '',
      profilePictureFile: null,
    });

    let perms = [...ALL_PERMISSION_VALUES];
    if (admin.permissions?.length) {
      if (admin.permissions.includes('all')) {
        setPermissionAccessType('all');
        perms = [...ALL_PERMISSION_VALUES];
      } else {
        setPermissionAccessType('selected');
        const validPerms = new Set();
        admin.permissions.forEach((p) => {
          if (ALL_PERMISSION_VALUES.includes(p)) {
            validPerms.add(p);
          }
        });
        perms = Array.from(validPerms);
      }
    } else {
      setPermissionAccessType('all');
    }

    setSelectedPermissions(perms);
    setRegions(regionsFromAdmin(admin));
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditMode(false);
    setSelectedAdminId(null);
    setFormData({
      name: '',
      email: '',
      phoneNumber: '',
      profilePicture: '',
      profilePictureFile: null,
    });
    setPermissionAccessType('all');
    setSelectedPermissions([...ALL_PERMISSION_VALUES]);
    setRegions([isIndia ? 'india' : 'qatar']);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    revokePreviewUrl(formData.profilePicture);
    setIsDialogOpen(false);
    setEditMode(false);
    setSelectedAdminId(null);
    setFormData({ name: '', email: '', phoneNumber: '', profilePicture: '', profilePictureFile: null });
    setPermissionAccessType('all');
    setSelectedPermissions([...ALL_PERMISSION_VALUES]);
    setRegions([isIndia ? 'india' : 'qatar']);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.name.trim()) {
        toast.error('Name is required');
        setLoading(false);
        return;
      }

      if (!formData.email) {
        toast.error('Please enter a valid email address');
        setLoading(false);
        return;
      }

      const phone = formData.phoneNumber.trim();
      if (phone && phone.length !== 8 && phone.length !== 10) {
        toast.error('Phone number must be 8 or 10 digits');
        setLoading(false);
        return;
      }

      if (!regions.length) {
        toast.error('Select at least one region');
        setLoading(false);
        return;
      }

      if (permissionAccessType === 'selected' && (!selectedPermissions || selectedPermissions.length === 0)) {
        toast.error('Please select at least one permission');
        setLoading(false);
        return;
      }

      let finalPermissions = selectedPermissions;
      if (permissionAccessType === 'all' || selectedPermissions.length === ALL_PERMISSION_VALUES.length) {
        finalPermissions = ['all'];
      }

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: phone || '',
        categoryAccess: 'all',
        permissions: finalPermissions,
        regions,
        profilePicture: formData.profilePicture,
        profilePictureFile: formData.profilePictureFile,
      };

      let result;

      if (editMode) {
        result = await authAPI.updateAdmin(selectedAdminId, payload);
      } else {
        result = await authAPI.createAdmin(payload);
      }

      if (result.success) {
        toast.success(editMode ? 'Admin updated successfully!' : 'Admin created successfully!');

        if (editMode && selectedAdminId === adminData?._id) {
          const verified = await authAPI.verify();
          if (verified.success && verified.data) {
            localStorage.setItem('adminData', JSON.stringify(verified.data));
            if (setAdminData) setAdminData(verified.data);
          }
        }

        handleCloseDialog();
        fetchAdmins(currentPage);
      } else {
        toast.error(result.error || (editMode ? 'Failed to update admin' : 'Failed to create admin'));
      }
    } catch (error) {
      console.error('Error submitting admin:', error);
      toast.error(`An error occurred while ${editMode ? 'updating' : 'creating'} the admin`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.adminId) return;

    try {
      setIsDeleting(true);
      const result = await authAPI.deleteAdmin(deleteModal.adminId);

      if (result.success) {
        toast.success(`Admin '${deleteModal.adminName}' deleted successfully`);
        setDeleteModal({ isOpen: false, adminId: null, adminName: '' });
        fetchAdmins(currentPage);
      } else {
        toast.error(result.error || 'Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error('An error occurred while deleting the admin');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (admin) => {
    setDeleteModal({
      isOpen: true,
      adminId: admin._id,
      adminName: admin.name,
    });
  };

  const backendUrl = getApiOrigin();

  const getProfilePicUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('blob:') || path.startsWith('http')) return path;
    return `${backendUrl}${path}`;
  };

  return (
    <div className="adminlist-page">
      <div className="adminlist-page-header">
        <div className="adminlist-page-title">
          <h1>Admins</h1>
          <p>View and manage administrators</p>
        </div>
      </div>

      <div className="adminlist-section-card">
        <div className="adminlist-section-header">
          <h2>Admin Users</h2>
          {canCreate && (
            <button className="adminlist-create-btn" onClick={handleAddNew}>
              Add New Admin
            </button>
          )}
        </div>

        <div className="adminlist-table-container">
        {adminLoading ? (
          <div className="adminlist-loading-state">
            <div className="adminlist-loading-spinner"></div>
            <p>Loading admins...</p>
          </div>
        ) : adminError ? (
          <div className="adminlist-error-state">
            <p>{adminError}</p>
            <button
              onClick={() => fetchAdmins(currentPage)}
              className="adminlist-retry-button"
            >
              Retry
            </button>
          </div>
        ) : admins.length === 0 ? (
          <div className="adminlist-empty-state">
            <p>No admins found</p>
          </div>
        ) : (
          <>
            <div className="adminlist-table-wrapper">
              <table className="adminlist-table">
                <thead>
                  <tr>
                    <th className="adminlist-pic-header">Pic</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th>Last Online</th>
                    {(canEdit || canDelete) && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin._id}>
                      <td className="adminlist-pic-cell">
                        <div className="adminlist-table-pic-wrapper">
                          <img 
                            src={getProfilePicUrl(admin.profilePicture) || '/images/default_admin_pic.jpeg'} 
                            alt={admin.name} 
                            className="adminlist-table-pic"
                            onError={(e) => { e.target.src = '/images/default_admin_pic.jpeg'; }}
                          />
                        </div>
                      </td>
                      <td className="adminlist-name-cell">
                        <span className="adminlist-name-text">{admin.name}</span>
                      </td>
                      <td>{admin.email}</td>
                      <td>{admin.phoneNumber || '-'}</td>
                      <td><span className="adminlist-role-badge">{admin.role}</span></td>
                      <td>
                        <span className={`adminlist-permission-badge ${admin.permissions?.includes('all') ? 'all' : ''}`}>
                          {admin.permissions?.includes('all') 
                            ? 'All Permissions' 
                            : `${admin.permissions?.length || 0} Permission${admin.permissions?.length !== 1 ? 's' : ''}`}
                        </span>
                      </td>
                      <td>{admin.lastOnline || '-'}</td>
                      {(canEdit || canDelete) && (
                        <td>
                          <div className="adminlist-actions-cell">
                            {canEdit && (
                              <button
                                onClick={() => handleEdit(admin)}
                                className="adminlist-edit-btn"
                                title="Edit Admin"
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => openDeleteModal(admin)}
                                className="adminlist-delete-btn"
                                title="Delete Admin"
                                disabled={admin._id === adminData?._id}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="adminlist-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev || adminLoading}
                  className="adminlist-pagination-button"
                >
                  Previous
                </button>

                <span className="adminlist-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext || adminLoading}
                  className="adminlist-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>

      {isDialogOpen && (
        <div className="adminlist-dialog-overlay">
          <div className="adminlist-dialog-box">
            <div className="adminlist-dialog-header">
              <div>
                <h2>{editMode ? 'Edit Admin' : 'Add New Admin'}</h2>
              </div>
              <button className="adminlist-close-btn" onClick={handleCloseDialog}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="adminlist-form">
              {editMode && (
                <div className="adminlist-profile-section">
                  <div className="adminlist-profile-upload-wrapper">
                    <div className="adminlist-profile-upload-container">
                      <div className="adminlist-profile-preview-container">
                        <img
                          src={getProfilePicUrl(formData.profilePicture) || '/images/default_admin_pic.jpeg'}
                          alt={formData.name || 'Admin'}
                          className="adminlist-profile-upload-preview"
                          onError={(e) => { e.target.src = '/images/default_admin_pic.jpeg'; }}
                        />
                      </div>
                      <label htmlFor="profilePicture" className="adminlist-profile-edit-badge" title="Upload Photo">
                        <Pencil size={14} />
                        <input
                          type="file"
                          id="profilePicture"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="adminlist-form-section">
                <div className="adminlist-form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter admin name"
                  />
                </div>

                <div className="adminlist-form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    disabled={editMode}
                  />
                </div>
              </div>

              <div className="adminlist-form-group">
                <label htmlFor="phoneNumber">Phone number <span className="adminlist-optional">(Optional)</span></label>
                <div className="adminlist-phone-input">
                  <span className="adminlist-country-code">{countryCode}</span>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder={`Enter ${phoneDigits}-digit phone number`}
                    maxLength={phoneDigits}
                  />
                </div>
              </div>

              <div className="adminlist-form-group">
                <label>Regions</label>
                <div className="adminlist-radio-group">
                  <label className="adminlist-radio-label">
                    <input
                      type="checkbox"
                      checked={regions.includes('qatar')}
                      onChange={() => setRegions((prev) => (
                        prev.includes('qatar') ? prev.filter((item) => item !== 'qatar') : [...prev, 'qatar']
                      ))}
                    />
                    <span>Qatar</span>
                  </label>
                  <label className="adminlist-radio-label">
                    <input
                      type="checkbox"
                      checked={regions.includes('india')}
                      onChange={() => setRegions((prev) => (
                        prev.includes('india') ? prev.filter((item) => item !== 'india') : [...prev, 'india']
                      ))}
                    />
                    <span>India</span>
                  </label>
                </div>
              </div>

              {/* Permissions */}
              <div className="adminlist-form-group">
                <label>Permissions</label>
                <div className="adminlist-radio-group">
                  <label className="adminlist-radio-label">
                    <input
                      type="radio"
                      name="permissionAccess"
                      value="all"
                      checked={permissionAccessType === 'all'}
                      onChange={() => {
                        setPermissionAccessType('all');
                        setSelectedPermissions([...ALL_PERMISSION_VALUES]);
                      }}
                    />
                    <span>All Permissions</span>
                  </label>
                  <label className="adminlist-radio-label">
                    <input
                      type="radio"
                      name="permissionAccess"
                      value="selected"
                      checked={permissionAccessType === 'selected'}
                      onChange={() => setPermissionAccessType('selected')}
                    />
                    <span>Selected Permissions</span>
                  </label>
                </div>

                {permissionAccessType === 'selected' && (
                  <div className="adminlist-category-access-section">
                    <div className="adminlist-choose-btn-container">
                      <button
                        type="button"
                        className="adminlist-choose-btn"
                        onClick={openPermissionsModal}
                      >
                        Choose Permissions
                      </button>
                    </div>
                    <div className="adminlist-selected-items">
                      <div className="adminlist-selected-categories-display">
                        <div>
                          {selectedPermissions.length} permission{selectedPermissions.length !== 1 ? 's' : ''} selected
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="adminlist-form-actions">
                <button
                  type="button"
                  className="adminlist-cancel-btn"
                  onClick={handleCloseDialog}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="adminlist-submit-btn"
                  disabled={loading}
                >
                  {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Admin' : 'Create Admin')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, adminId: null, adminName: '' })}
        onConfirm={handleDelete}
        title="Delete Admin"
        message={`Are you sure you want to delete admin '${deleteModal.adminName}'? This action cannot be undone.`}
        isLoading={isDeleting}
      />

      {isPermissionsModalOpen && (
        <div className="adminlist-dialog-overlay" style={{ zIndex: 1001 }}>
          <div className="adminlist-dialog-box adminlist-perm-dialog">
            <div className="adminlist-dialog-header">
              <h2>Select Permissions</h2>
              <button className="adminlist-close-btn" onClick={() => setIsPermissionsModalOpen(false)}>&times;</button>
            </div>

            <div className="adminlist-cat-search-section">
              <input
                type="text"
                placeholder="Search permissions..."
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                className="adminlist-cat-search-input"
              />
            </div>

            <div className="adminlist-perm-modal-toolbar">
              <label className="adminlist-perm-select-all-label">
                <input
                  type="checkbox"
                  checked={allTempSelected}
                  onChange={toggleAllPermissions}
                />
                <span>Select All</span>
              </label>
              <span className="adminlist-perm-modal-count">
                {tempSelectedPermissions.length} / {ALL_PERMISSION_VALUES.length} selected
              </span>
            </div>

            <div className="adminlist-perm-modal-body">
              {filteredPermissions.length === 0 ? (
                <div className="adminlist-empty-state" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  <p>No permissions found for "{permissionSearch}"</p>
                </div>
              ) : (
                <div className="adminlist-perm-modal-grid">
                  {filteredPermissions.sort((a, b) => {
                    const aSelected = selectedPermissions.includes(a) ? 1 : 0;
                    const bSelected = selectedPermissions.includes(b) ? 1 : 0;
                    return bSelected - aSelected;
                  }).map((p) => {
                    const active = tempSelectedPermissions.includes(p);
                    return (
                      <label
                        key={p}
                        className={`adminlist-perm-modal-card ${active ? 'adminlist-perm-modal-card--active' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleTempPermission(p)}
                        />
                        <span className="adminlist-perm-modal-label">{formatPermissionLabel(p)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="adminlist-cat-footer">
              <div className="adminlist-cat-selection-count"></div>
              <div className="adminlist-cat-footer-actions">
                <button type="button" className="adminlist-cancel-btn" onClick={() => setIsPermissionsModalOpen(false)}>Cancel</button>
                <button type="button" className="adminlist-submit-btn" onClick={savePermissionsSelection}>Save Selection</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;
